import type { ProductFormSchema } from "schemas/generated/product";

import React from "react";

import Input from "@editor/components/common/designSystem/Input";
import LabeledControl from "@editor/components/common/designSystem/LabeledControl";
import { MinimalTiptap } from "@editor/components/MinimalTipTapEditor";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { routes } from "@editor/utils/router";

import ProductMediaDropZone from "@/features/products/components/ProductMediaDropZone";
import { ProductTypeIcon } from "@/features/products/components/ProductTypeIcon";
import { PRODUCT_TYPE_OPTIONS } from "@/features/products/constants/product-type-options";
import { useProductFormEdit } from "@/features/products/hooks/useProductFormEdit";
import { useProductUpdate } from "@/features/products/hooks/useProductUpdate";
import Button from "@replo/design-system/components/button/Button";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { LargeMenuItem } from "@replo/design-system/components/menu/LargeMenuItem";
import { useIsMobile } from "@replo/design-system/components/shadcn/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowLeft } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";

function ProductFormHeader() {
  const navigate = useNavigate();

  return (
    <div className="flex items-center w-full">
      <Button
        variant="tertiary"
        size="base"
        startEnhancer={<ArrowLeft size={16} />}
        onClick={() => navigate(-1)}
      >
        Back
      </Button>
    </div>
  );
}

export function ProductEditForm() {
  const {
    watch,
    dispatchProductEdit,
    handleReactHookFormSubmit,
    errors,
    isProductValid: isValid,
    hasDataChanged: isDirty,
    creatingNewProduct,
    reset,
    product,
  } = useProductFormEdit();

  const { updateProduct } = useProductUpdate();
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();
  const isMobile = useIsMobile();

  const handlePriceBlur = React.useCallback(
    (priceType: "price" | "compareAtPrice") => {
      const currentValue = watch(priceType);
      if (currentValue == null) {
        return;
      }
      const roundedValue = Number.parseFloat(currentValue.toFixed(2));
      if (roundedValue !== currentValue) {
        dispatchProductEdit({
          type: "changeProperty",
          payload: { key: priceType, value: roundedValue },
        });
      }
    },
    [dispatchProductEdit, watch],
  );

  const handleProductSave = React.useCallback(
    async (productData: ProductFormSchema) => {
      // NOTE (Kurt, 2025-09-19): We keep the externalCheckoutUrl in the form data
      // even if the checkout method is replo to have a better form experience; however
      // when the product is saved, we need to set the externalCheckoutUrl to null if the
      // checkout method is replo.
      const externalCheckoutUrl =
        productData.checkoutMethod === "external"
          ? productData.externalCheckoutUrl
          : null;

      const processedFormData = {
        ...productData,
        externalCheckoutUrl,
      };

      await updateProduct({
        product,
        formData: processedFormData,
        creatingNewProduct,
      });
      reset(productData);
      navigate(
        generatePath(routes.shops.products.list, {
          projectId: projectId ?? "",
        }),
      );
    },
    [updateProduct, product, creatingNewProduct, reset, navigate, projectId],
  );

  const selectedProductType = watch("productType");
  const selectedOption = PRODUCT_TYPE_OPTIONS.find(
    (option) => option.value === selectedProductType,
  );

  const selectableValue =
    selectedProductType === "UNASSIGNED" ? null : selectedProductType;

  const selectableProductOptions = PRODUCT_TYPE_OPTIONS.map((option) => ({
    value: option.value,
    label: (
      <LargeMenuItem
        variant="flexible"
        label={option.label}
        startEnhancer={
          <ProductTypeIcon icon={option.icon} color={option.color} />
        }
      />
    ),
    displayValue: option.label,
  }));

  return (
    <div
      className={twMerge(
        "shadow-selectable border-0.5 border-border rounded-[10px] bg-white flex flex-col items-center gap-4 p-5",
        isMobile ? "w-full" : "w-[600px]",
      )}
    >
      <div className="flex flex-col w-full gap-2">
        <ProductFormHeader />
        <div className="flex items-center justify-between w-full gap-4">
          <div className="header-h1 truncate whitespace-nowrap min-w-0">
            {creatingNewProduct ? "New Product" : `${watch("name")}`}
          </div>
          <Button
            variant="primary"
            size="base"
            onClick={() => handleReactHookFormSubmit(handleProductSave)()}
            disabled={!isValid || !isDirty}
            UNSAFE_className="flex-shrink-0"
          >
            {creatingNewProduct ? "Create Product" : "Save Product"}
          </Button>
        </div>
      </div>
      <div
        className={twMerge(
          "flex w-full",
          isMobile ? "flex-col gap-4" : "gap-6",
        )}
      >
        <LabeledControl
          label="Product Name"
          error={errors.name?.message}
          size="base"
          containerClassName="flex-1 min-w-0"
          required
        >
          <Input
            value={watch("name")}
            onChange={(e) =>
              dispatchProductEdit({
                type: "changeProperty",
                payload: { key: "name", value: e.target.value },
              })
            }
            placeholder="Enter product name"
            size="base"
            validityState={errors.name ? "invalid" : "valid"}
            layoutClassName="w-full"
          />
        </LabeledControl>

        <LabeledControl
          label="Product Type"
          size="base"
          containerClassName="flex-1 min-w-0"
        >
          <div className="w-full">
            <Combobox.Root
              options={selectableProductOptions}
              value={selectableValue ?? undefined}
              onChange={(value) =>
                dispatchProductEdit({
                  type: "changeProperty",
                  payload: { key: "productType", value },
                })
              }
            >
              <Combobox.Trigger>
                <Combobox.SelectionButton
                  id="product-type-selector"
                  title={
                    selectedOption
                      ? selectedOption.label
                      : "Select product type"
                  }
                  isPlaceholder={!selectedOption}
                  startEnhancer={
                    selectedOption ? (
                      <div className="bg-surface flex h-5 w-5 shrink-0 items-center justify-center rounded-sm">
                        <ProductTypeIcon
                          icon={selectedOption.icon}
                          color={selectedOption.color}
                        />
                      </div>
                    ) : undefined
                  }
                  size="base"
                  variant="primary"
                  titleAlignment="start"
                />
              </Combobox.Trigger>
              <Combobox.Popover unsafe_className="py-0.5">
                <Combobox.Content areOptionsSearchable={false} />
              </Combobox.Popover>
            </Combobox.Root>
          </div>
        </LabeledControl>
      </div>
      <div
        className={twMerge(
          "flex w-full",
          isMobile ? "flex-col gap-4" : "gap-6",
        )}
      >
        <LabeledControl
          label="Price"
          size="base"
          containerClassName="flex-1 min-w-0"
          error={errors.price?.message}
          required
        >
          <Input
            value={watch("price")?.toString() ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              const numericValue =
                value === "" ? null : Number.parseFloat(value);
              dispatchProductEdit({
                type: "changeProperty",
                payload: { key: "price", value: numericValue },
              });
            }}
            onBlur={() => handlePriceBlur("price")}
            placeholder="0.00"
            size="base"
            type="number"
            inputMode="decimal"
            startEnhancer={<span className="text-muted">$</span>}
            validityState={errors.price ? "invalid" : "valid"}
            layoutClassName="w-full"
          />
        </LabeledControl>
        <LabeledControl
          label="Compare at Price"
          size="base"
          containerClassName="flex-1 min-w-0"
        >
          <Input
            value={watch("compareAtPrice")?.toString() ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              const numericValue =
                value === "" ? null : Number.parseFloat(value);
              dispatchProductEdit({
                type: "changeProperty",
                payload: { key: "compareAtPrice", value: numericValue },
              });
            }}
            onBlur={() => handlePriceBlur("compareAtPrice")}
            placeholder="0.00"
            size="base"
            type="number"
            inputMode="decimal"
            startEnhancer={<span className="text-muted">$</span>}
            layoutClassName="w-full"
          />
        </LabeledControl>
      </div>
      <div className="w-full">
        <LabeledControl
          label="Description"
          size="base"
          containerClassName="w-full"
        >
          <MinimalTiptap
            content={watch("description") ?? ""}
            onChange={(value) =>
              dispatchProductEdit({
                type: "changeProperty",
                payload: { key: "description", value },
              })
            }
            placeholder="Enter product description"
            className="w-full text-base font-normal"
          />
        </LabeledControl>
      </div>
      <LabeledControl label="Media" size="base" containerClassName="w-full">
        <ProductMediaDropZone />
      </LabeledControl>
    </div>
  );
}
